<template>
  <message-box
    v-if="displayMsgBox && currentAction === 'envelope'"
    :title="modalTitle"
    submit-text="Send external message"
    type="green"
    :disable-submit="disableSubmitMsg()"
    @on-close="setDisplayMsgBox(false)"
    @on-cancel="handleCancelMessage"
    @on-submit="handleSubmitMessage"
  >
    <div class="message-contents-field">
      <NrSelect
        v-if="canReadStaffOnlyItems"
        v-model="selectedMessageTemplates"
        placeholder="Search and select message template(s)"
        :options="filteredMessageTemplates"
        :filterable="false"
        :loading="dataFetchState.message_templates.fetching"
        :hasMore="!dataFetchState.message_templates.end"
        @option:deselected="setMessageTemplate(true, $event)"
        @option:selected="setMessageTemplate(false, $event)"
        @open="searchMessageTemplates"
        @search="searchMessageTemplates"
        @load-more="loadMoreMessageTemplates"
        class="m-top-12"
        multiple
      >
        <template v-slot:label>Message Template</template>
      </NrSelect>
      <NrFormLabel>Message contents</NrFormLabel>
      <nr-editor
        v-model="message"
        :resizable="false"
        :placeholder="
          canReadStaffOnlyItems
            ? 'Type your message here or select a template above.'
            : 'Type your message here.'
        "
        :rows="12"
        strike
        bulletList
        orderedList
        link
      />
      <p
        class="is-size-7 m-bottom-12"
        data-cy="external-message-editor-footnote"
      >
        <span v-if="this.canReadStaffOnlyItems && this.requesterEmailAvailable">
          This message will be visible to the requester and staff.
        </span>
        <span
          v-else-if="
            this.canReadStaffOnlyItems && !this.requesterEmailAvailable
          "
        >
          <div class="notification is-warning footer">
            Requester email not available - posts to timeline only. The
            requester will only be able to see your message if they contact your
            agency to have their email associated with this request.
          </div>
        </span>
        <span v-else>
          Your message will be sent to staff working on this request and posted
          to the request timeline.
        </span>
      </p>
      <NrFormInput
        v-if="canReadStaffOnlyItems && requesterEmailAvailable"
        v-model="messageRequired"
        id="send-to-requester-checkbox"
        type="checkbox"
        label="Yes, send to requester (required)"
      />
    </div>
  </message-box>
</template>

<script lang="ts">
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import MessageBox from '@nextrequestco/components/src/components/MessageBox'
import NrEditor from '@nextrequestco/components/src/components/NrEditor'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements'
import { MessageTemplates } from './types/message_boxes'
import { mapActions, mapState } from 'vuex'
import throttle from 'lodash/throttle'

export default {
  name: 'ExternalMessageModal',
  components: {
    MessageBox,
    NrFormInput,
    'nr-editor': NrEditor,
    NrFormLabel,
    NrSelect
  },
  data() {
    return {
      selectedMessageTemplates: [],
      message: '',
      messageRequired: false
    }
  },
  computed: {
    ...mapState('request_show', [
      'displayMsgBox',
      'currentAction',
      'request',
      'dataOptions',
      'dataFetchState'
    ]),
    ...mapState(['account']),

    requesterEmailAvailable(): boolean {
      return this.request.requester.email !== 'Email Not Available'
    },

    canReadStaffOnlyItems(): boolean {
      return this.$ability.can('read', 'staff_only_items')
    },

    filteredMessageTemplates(): Array<MessageTemplates> {
      return this.dataOptions.message_templates.map(
        (temp: MessageTemplates) => ({
          value: temp.id,
          label: temp.message_template_name,
          description: temp.message_template_description
        })
      )
    },

    modalTitle(): string {
      return this.canReadStaffOnlyItems
        ? `Message to ${this.request.requester.name}`
        : `Message to ${this.account.name}`
    }
  },
  methods: {
    ...mapActions('request_show', [
      'submitExternalMessage',
      'setDisplayMsgBox',
      'fetchDataOptions'
    ]),

    loadMoreMessageTemplates: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.message_templates.search_text,
        dataType: 'message_templates'
      })
    }, 400),

    disableSubmitMsg(): boolean {
      if (this.canReadStaffOnlyItems && this.requesterEmailAvailable) {
        return !(this.messageRequired && this.message.length)
      } else {
        return !this.message.length
      }
    },

    setMessageTemplate(reset = false, templates = []): void {
      if (reset) {
        this.message = this.selectedMessageTemplates
          .map((temp: { description: string }) => temp.description)
          .join('')
      } else {
        this.message = this.message + templates.pop()?.description
      }
    },

    searchMessageTemplates: throttle(function (searchText = '') {
      this.fetchDataOptions({
        searchText,
        dataType: 'message_templates'
      })
    }, 400),

    handleCancelMessage(): void {
      this.message = ''
      this.selectedMessageTemplates = []
      this.messageRequired = false
      setTimeout(() => {
        this.setDisplayMsgBox(false)
      }, 100)
    },

    handleSubmitMessage(): void {
      this.submitExternalMessage({
        note_text: this.message,
        note_type: 'requester',
        state: 'requester',
        request_id: this.request.pretty_id,
        required: this.messageRequired,
        message_template_ids: this.selectedMessageTemplates.map(
          (template: { value: number }) => template.value
        )
      }).then(() => {
        this.handleCancelMessage()
      })
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/colors.scss';

.message-box {
  .footer {
    font-size: 14px;
    font-weight: 600;
    color: $black;
    padding-top: 15px;
    padding-bottom: 15px;
  }
  .message-contents-field,
  .editor-field {
    display: flex;
    flex-direction: column;
    flex: 0 1 100%;
  }

  .nr-editor {
    flex: 1 1 auto;
  }

  #send-to-requester-checkbox {
    justify-content: flex-end;
  }
}
</style>
